import { MapTypeStyle, MapTypeId } from 'bits/map'

interface MapTheme {
  styles?: MapTypeStyle[]
  mapTypeId?: string
  id: number
  markerColor: string
  previewImageUrl: string
}

const GOOGLE_STATIC_MAPS_API_PREFIX =
  'https://maps.googleapis.com/maps/api/staticmap?key=AIzaSyD3kjetwpeAYF-eXThlRhc1F1EYwsQvHcY&center={LATITUDE},{LONGITUDE}&zoom={ZOOM}&format=png&size=960x720'
const createPreviewImageHash = (theme: MapTheme): string => {
  const styles = theme.styles || []
  const mapTypeId = theme.mapTypeId || 'roadmap'
  return GOOGLE_STATIC_MAPS_API_PREFIX + '&' + styles.map((style): string => {
    const feature = style.featureType ? `feature:${style.featureType}` : null
    const element = style.elementType ? `element:${style.elementType}` : null
    const stylers = style.stylers && style.stylers.length > 0 ? style.stylers.map((styler): string => Object.keys(styler).map((stylingKey): string => `${stylingKey}:${styler[stylingKey]}`).join('%7C')).join('%7C').replace('#', '0x') : null
    const isNotNull = (x: any): boolean => !!x
    return 'style=' + [feature, element, stylers].filter(isNotNull).join('%7C') // encoded vertical separator |
  }).join('&') + '&maptype=' + mapTypeId
}

// Default Apple-like Theme - https://snazzymaps.com/style/281616/our-apple-like-style
const THE_USUAL: MapTheme = {
  id: 100,
  previewImageUrl: '',
  markerColor: 'ff0000', // #ff0000
  styles: [
    {
      featureType: 'landscape.man_made',
      elementType: 'geometry',
      stylers: [
        {
          color: '#f7f1df',
        },
      ],
    },
    {
      featureType: 'landscape.natural',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#f7f1df',
        },
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'landscape.natural.landcover',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#d0e3b4',
        },
        {
          visibility: 'simplified',
        },
      ],
    },
    {
      featureType: 'landscape.natural.terrain',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#c7dcab',
        },
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'poi.business',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'poi.medical',
      elementType: 'geometry',
      stylers: [
        {
          color: '#fbd3da',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry',
      stylers: [
        {
          color: '#bde6ab',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry.stroke',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'labels',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'road.arterial',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#ffffff',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#ffe15f',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#efd151',
        },
      ],
    },
    {
      featureType: 'transit.station.airport',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#cfb2db',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [
        {
          color: '#a2daf2',
        },
      ],
    },
  ],
}

// Google Silver - https://mapstyle.withgoogle.com
const SILVER: MapTheme = {
  id: 101,
  previewImageUrl: '',
  markerColor: '000000', // #000000
  styles: [
    {
      elementType: 'geometry',
      stylers: [
        {
          color: '#f5f5f5',
        },
      ],
    },
    {
      elementType: 'labels.icon',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#616161',
        },
      ],
    },
    {
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#f5f5f5',
        },
      ],
    },
    {
      featureType: 'administrative.land_parcel',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#bdbdbd',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'geometry',
      stylers: [
        {
          color: '#eeeeee',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry',
      stylers: [
        {
          color: '#e5e5e5',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#9e9e9e',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [
        {
          color: '#ffffff',
        },
      ],
    },
    {
      featureType: 'road.arterial',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [
        {
          color: '#dadada',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#616161',
        },
      ],
    },
    {
      featureType: 'road.local',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#9e9e9e',
        },
      ],
    },
    {
      featureType: 'transit.line',
      elementType: 'geometry',
      stylers: [
        {
          color: '#e5e5e5',
        },
      ],
    },
    {
      featureType: 'transit.station',
      elementType: 'geometry',
      stylers: [
        {
          color: '#eeeeee',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [
        {
          color: '#c9c9c9',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#9e9e9e',
        },
      ],
    },
  ],
}

// Google Dark - https://mapstyle.withgoogle.com
const BLACK_METAL: MapTheme = {
  id: 102,
  previewImageUrl: '',
  markerColor: 'ffffff', // #ffffff
  styles: [
    {
      elementType: 'geometry',
      stylers: [
        {
          color: '#212121',
        },
      ],
    },
    {
      elementType: 'labels.icon',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575',
        },
      ],
    },
    {
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#212121',
        },
      ],
    },
    {
      featureType: 'administrative',
      elementType: 'geometry',
      stylers: [
        {
          color: '#757575',
        },
      ],
    },
    {
      featureType: 'administrative.country',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#9e9e9e',
        },
      ],
    },
    {
      featureType: 'administrative.land_parcel',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'administrative.locality',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#bdbdbd',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry',
      stylers: [
        {
          color: '#181818',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#616161',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#1b1b1b',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#2c2c2c',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#8a8a8a',
        },
      ],
    },
    {
      featureType: 'road.arterial',
      elementType: 'geometry',
      stylers: [
        {
          color: '#373737',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [
        {
          color: '#3c3c3c',
        },
      ],
    },
    {
      featureType: 'road.highway.controlled_access',
      elementType: 'geometry',
      stylers: [
        {
          color: '#4e4e4e',
        },
      ],
    },
    {
      featureType: 'road.local',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#616161',
        },
      ],
    },
    {
      featureType: 'transit',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [
        {
          color: '#000000',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#3d3d3d',
        },
      ],
    },
  ],
}

// Medieval Style - https://snazzymaps.com/style/272010/vintage
const VINTAGE: MapTheme = {
  id: 103,
  previewImageUrl: '',
  markerColor: '000000', // #000000
  styles: [
    {
      stylers: [
        {
          color: '#ff7000',
        },
        {
          saturation: 100,
        },
        {
          lightness: 69,
        },
        {
          gamma: 2.04,
        },
        {
          weight: 1.17,
        },
      ],
    },
    {
      elementType: 'geometry',
      stylers: [
        {
          color: '#cb8536',
        },
      ],
    },
    {
      elementType: 'labels',
      stylers: [
        {
          color: '#ffb471',
        },
        {
          saturation: 100,
        },
        {
          lightness: 66,
        },
      ],
    },
    {
      elementType: 'labels.icon',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      elementType: 'labels.text',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#000000',
        },
      ],
    },
    {
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#e7c284',
        },
        {
          saturation: -31,
        },
        {
          lightness: -33,
        },
        {
          gamma: 0.8,
        },
        {
          visibility: 'on',
        },
        {
          weight: 2,
        },
      ],
    },
    {
      featureType: 'administrative.province',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#6a4f08',
        },
      ],
    },
    {
      featureType: 'landscape',
      stylers: [
        {
          saturation: 26,
        },
        {
          lightness: -8,
        },
        {
          gamma: 0.98,
        },
        {
          weight: 2.45,
        },
      ],
    },
    {
      featureType: 'landscape',
      elementType: 'geometry',
      stylers: [
        {
          saturation: 30,
        },
        {
          lightness: 30,
        },
      ],
    },
    {
      featureType: 'landscape.man_made',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#d6b169',
        },
      ],
    },
    {
      featureType: 'landscape.natural',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#d7a663',
        },
      ],
    },
    {
      featureType: 'landscape.natural.landcover',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#c08747',
        },
      ],
    },
    {
      featureType: 'landscape.natural.terrain',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#c69253',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'geometry',
      stylers: [
        {
          saturation: 20,
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry',
      stylers: [
        {
          saturation: -20,
        },
        {
          lightness: 20,
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [
        {
          saturation: -30,
        },
        {
          lightness: 10,
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry.stroke',
      stylers: [
        {
          saturation: 25,
        },
        {
          lightness: 25,
        },
      ],
    },
    {
      featureType: 'water',
      stylers: [
        {
          color: '#ecc080',
        },
        {
          lightness: -20,
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#f2e0bf',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#c9aa6f',
        },
      ],
    },
  ],
}

// Retro style - https://mapstyle.withgoogle.com
const RETRO: MapTheme = {
  id: 104,
  previewImageUrl: '',
  markerColor: '4e3836', // #4e3836
  styles: [
    {
      elementType: 'geometry',
      stylers: [
        {
          color: '#ebe3cd',
        },
      ],
    },
    {
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#523735',
        },
      ],
    },
    {
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#f5f1e6',
        },
      ],
    },
    {
      featureType: 'administrative',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#c9b2a6',
        },
      ],
    },
    {
      featureType: 'administrative.land_parcel',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#dcd2be',
        },
      ],
    },
    {
      featureType: 'administrative.land_parcel',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#ae9e90',
        },
      ],
    },
    {
      featureType: 'landscape.natural',
      elementType: 'geometry',
      stylers: [
        {
          color: '#dfd2ae',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'geometry',
      stylers: [
        {
          color: '#dfd2ae',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#93817c',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#a5b076',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#447530',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [
        {
          color: '#f5f1e6',
        },
      ],
    },
    {
      featureType: 'road.arterial',
      elementType: 'geometry',
      stylers: [
        {
          color: '#fdfcf8',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [
        {
          color: '#f8c967',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#e9bc62',
        },
      ],
    },
    {
      featureType: 'road.highway.controlled_access',
      elementType: 'geometry',
      stylers: [
        {
          color: '#e98d58',
        },
      ],
    },
    {
      featureType: 'road.highway.controlled_access',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#db8555',
        },
      ],
    },
    {
      featureType: 'road.local',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#806b63',
        },
      ],
    },
    {
      featureType: 'transit.line',
      elementType: 'geometry',
      stylers: [
        {
          color: '#dfd2ae',
        },
      ],
    },
    {
      featureType: 'transit.line',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#8f7d77',
        },
      ],
    },
    {
      featureType: 'transit.line',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#ebe3cd',
        },
      ],
    },
    {
      featureType: 'transit.station',
      elementType: 'geometry',
      stylers: [
        {
          color: '#dfd2ae',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#b9d3c2',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#92998d',
        },
      ],
    },
  ],
}

// Tron like with neon color features - https://snazzymaps.com/style/271969/tron
const TRON: MapTheme = {
  id: 105,
  previewImageUrl: '',
  markerColor: 'd7d253', // #d7d253
  styles: [
    {
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#ffffff',
        },
      ],
    },
    {
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#000000',
        },
        {
          lightness: 13,
        },
      ],
    },
    {
      featureType: 'administrative',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#000000',
        },
      ],
    },
    {
      featureType: 'administrative',
      elementType: 'geometry.stroke',
      stylers: [
        {
          lightness: 14,
        },
        {
          weight: 1.4,
        },
      ],
    },
    {
      featureType: 'administrative.province',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#e434e6',
        },
      ],
    },
    {
      featureType: 'landscape',
      stylers: [
        {
          color: '#364d6d',
        },
      ],
    },
    {
      featureType: 'landscape',
      elementType: 'geometry.stroke',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'landscape.natural.terrain',
      elementType: 'geometry.fill',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'geometry',
      stylers: [
        {
          color: '#0c4152',
        },
        {
          lightness: 5,
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.icon',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#294876',
        },
      ],
    },
    {
      featureType: 'road.arterial',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#b432af',
        },
      ],
    },
    {
      featureType: 'road.arterial',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#0b3d51',
        },
        {
          lightness: 16,
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#00dfff',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#00dfff',
        },
        {
          lightness: 25,
        },
      ],
    },
    {
      featureType: 'road.local',
      elementType: 'geometry',
      stylers: [
        {
          color: '#000000',
        },
      ],
    },
    {
      featureType: 'road.local',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#146474',
        },
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'road.local',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#146474',
        },
      ],
    },
    {
      featureType: 'transit',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#146474',
        },
      ],
    },
    {
      featureType: 'transit',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#146474',
        },
      ],
    },
    {
      featureType: 'transit',
      elementType: 'labels.icon',
      stylers: [
        {
          saturation: -30,
        },
        {
          lightness: -35,
        },
        {
          gamma: 1,
        },
      ],
    },
    {
      featureType: 'transit',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#146474',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#34335b',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry.stroke',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
  ],
}

// Google Night Driving Style - https://mapstyle.withgoogle.com
const NIGHT: MapTheme = {
  id: 106,
  previewImageUrl: '',
  markerColor: 'dc9b66', // #dc9b66
  styles: [
    {
      elementType: 'geometry',
      stylers: [
        {
          color: '#242f3e',
        },
      ],
    },
    {
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#746855',
        },
      ],
    },
    {
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#242f3e',
        },
      ],
    },
    {
      featureType: 'administrative.locality',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#d59563',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#d59563',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry',
      stylers: [
        {
          color: '#263c3f',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#6b9a76',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [
        {
          color: '#38414e',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#212a37',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#9ca5b3',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [
        {
          color: '#746855',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#1f2835',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#f3d19c',
        },
      ],
    },
    {
      featureType: 'transit',
      elementType: 'geometry',
      stylers: [
        {
          color: '#2f3948',
        },
      ],
    },
    {
      featureType: 'transit.station',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#d59563',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [
        {
          color: '#17263c',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#515c6d',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#17263c',
        },
      ],
    },
  ],
}

// Colorful Amusement Park style - https://snazzymaps.com/style/272136/amusement-park
const AMUSEMENT_PARK: MapTheme = {
  id: 107,
  previewImageUrl: '',
  markerColor: 'ff0000', // #ff0000
  styles: [
    {
      featureType: 'administrative',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#000000',
        },
      ],
    },
    {
      featureType: 'administrative',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#ffffff',
        },
      ],
    },
    {
      featureType: 'administrative.province',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#ff0000',
        },
      ],
    },
    {
      featureType: 'landscape.man_made',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#ffec02',
        },
      ],
    },
    {
      featureType: 'landscape.natural',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#ffeb00',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#feff00',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels',
      stylers: [
        {
          visibility: 'simplified',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#00de00',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'all',
      stylers: [
        {
          visibility: 'simplified',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry.fill',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#ffd900',
        },
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'all',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [
        {
          color: '#ff8100',
        },
      ],
    },
    {
      featureType: 'transit.line',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#ffce00',
        },
      ],
    },
    {
      featureType: 'transit.line',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#ffeb00',
        },
      ],
    },
    {
      featureType: 'transit.line',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#554e00',
        },
      ],
    },
    {
      featureType: 'transit.station',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#ffec02',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#408cf5',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#004c8d',
        },
      ],
    },
  ],
}

// Minecraft inspired - https://snazzymaps.com/style/271959/minecraft
const VIDEO_GAME: MapTheme = {
  id: 108,
  previewImageUrl: '',
  markerColor: 'ff8a00', // #ff8a00
  styles: [
    {
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#ffffff',
        },
      ],
    },
    {
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#000000',
        },
      ],
    },
    {
      featureType: 'administrative.land_parcel',
      elementType: 'geometry.fill',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'administrative.province',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#ffffff',
        },
      ],
    },
    {
      featureType: 'landscape',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#378f19',
        },
      ],
    },
    {
      featureType: 'landscape.man_made',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#378f19',
        },
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'landscape.natural',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'landscape.natural',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#378f19',
        },
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#089100',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#297110',
        },
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'road',
      stylers: [
        {
          hue: '#ff0000',
        },
        {
          visibility: 'off',
        },
        {
          weight: 1.91,
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry.stroke',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'road.arterial',
      stylers: [
        {
          visibility: 'simplified',
        },
      ],
    },
    {
      featureType: 'road.arterial',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#71aa51',
        },
        {
          weight: 2.08,
        },
      ],
    },
    {
      featureType: 'road.highway',
      stylers: [
        {
          color: '#ffffff',
        },
        {
          visibility: 'off',
        },
        {
          weight: 3.34,
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#8c560f',
        },
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry.stroke',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'road.highway.controlled_access',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'road.local',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'road.local',
      elementType: 'geometry.fill',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'transit',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#3b3bea',
        },
      ],
    },
  ],
}

// Satellite by Google
const SATELLITE: MapTheme = {
  id: 109,
  previewImageUrl: '',
  markerColor: 'ff0000', // #ff0000
  mapTypeId: 'satellite',
  styles: THE_USUAL.styles,
}

// Watercolor by Stamen
const WATERCOLOR: MapTheme = {
  id: 110,
  previewImageUrl: '',
  markerColor: 'ff0000', // #ff0000
  mapTypeId: 'watercolor',
  styles: THE_USUAL.styles,
}

// THE_USUAL.previewImageUrl = createPreviewImageHash(THE_USUAL)
// SILVER.previewImageUrl = createPreviewImageHash(SILVER)
// BLACK_METAL.previewImageUrl = createPreviewImageHash(BLACK_METAL)
// VINTAGE.previewImageUrl = createPreviewImageHash(VINTAGE)
// RETRO.previewImageUrl = createPreviewImageHash(RETRO)
// TRON.previewImageUrl = createPreviewImageHash(TRON)
// NIGHT.previewImageUrl = createPreviewImageHash(NIGHT)
// AMUSEMENT_PARK.previewImageUrl = createPreviewImageHash(AMUSEMENT_PARK)
// VIDEO_GAME.previewImageUrl = createPreviewImageHash(VIDEO_GAME)
// SATELLITE.previewImageUrl = createPreviewImageHash(SATELLITE)
// WATERCOLOR.previewImageUrl = createPreviewImageHash(WATERCOLOR)

const ALL_THEMES: Record<ValidThemeId, MapTheme> = {
  100: THE_USUAL,
  101: SILVER,
  102: BLACK_METAL,
  103: VINTAGE,
  104: RETRO,
  105: TRON,
  106: NIGHT,
  107: AMUSEMENT_PARK,
  108: VIDEO_GAME,
  109: SATELLITE,
  110: WATERCOLOR,
}

enum ColorScheme {
  light = 'light',
  dark = 'dark'
}

const COLOR_SCHEME: Record<ValidThemeId, ColorScheme> = {
  100: ColorScheme.light,
  101: ColorScheme.light,
  102: ColorScheme.dark,
  103: ColorScheme.light,
  104: ColorScheme.light,
  105: ColorScheme.dark,
  106: ColorScheme.dark,
  107: ColorScheme.light,
  108: ColorScheme.light,
  109: ColorScheme.light,
  110: ColorScheme.light,
}

type ValidThemeId = 100 | 101 | 102 | 103 | 104 | 105 | 106 | 107 | 108 | 109 | 110

const THEME_IDS: ValidThemeId[] = [100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110]

function getCompatibleThemeId (themeId: number | string): ValidThemeId {
  const numberThemeId = Number(themeId) as ValidThemeId
  if (THEME_IDS.includes(numberThemeId)) {
    return numberThemeId
  }
  return THEME_IDS[0]
}

function getThemeFromThemeId (themeId: number | string): MapTheme {
  return ALL_THEMES[getCompatibleThemeId(themeId)]
}

function getStylesFromThemeId (themeId: number | string): MapTypeStyle[] {
  return getThemeFromThemeId(themeId).styles || []
}

function getMapTypeIdFromThemeId (themeId: number | string): MapTypeId {
  return (getThemeFromThemeId(themeId).mapTypeId || 'roadmap') as unknown as MapTypeId
}

// function getPreviewImageUrlFromThemeId (themeId: number | string): string {
//   const validThemeId = getCompatibleThemeId(themeId)
//   const latitude = 37.7376948
//   const longitude = -122.2327052
//   const zoom = 10
//   if (validThemeId === 110) {
//     return 'https://stamen-tiles.a.ssl.fastly.net/watercolor/10/163/395.jpg'
//   }
//   return getThemeFromThemeId(themeId).previewImageUrl
//     .replace('{LATITUDE},{LONGITUDE}', `${latitude},${longitude}`)
//     .replace('{ZOOM}', String(zoom))
// }

function getColorSchemeFromThemeId (themeId: number | string): string {
  return COLOR_SCHEME[getCompatibleThemeId(themeId)]
}

function getMarkerColorFromThemeId (themeId: number | string): string {
  return getThemeFromThemeId(themeId).markerColor.toLowerCase()
}
export {
  // types
  ValidThemeId,
  MapTheme,
  // helpers
  getCompatibleThemeId,
  getThemeFromThemeId,
  getStylesFromThemeId,
  getMapTypeIdFromThemeId,
  getMarkerColorFromThemeId,
  // getPreviewImageUrlFromThemeId,
  getColorSchemeFromThemeId,
}
